"use client";

import { ManageOrganizationsDialog } from "@/components/manage-organizations-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useAllUsers,
  useInvalidateUsers,
  useMyOrganizations,
  useOrgRoles,
  useOrgsRoles,
  useRefreshUsers,
} from "@/hooks/use-users";
import { toast } from "@/lib/instrumented-toast";
import { copyInviteLink, messageFromAxiosOrError } from "@/lib/invite-link";
import { queryKeys } from "@/lib/query-keys";
import { useApiInstance } from "@/providers/api-provider";
import { useOrgStore } from "@/stores";
import { ListUser, useUserStore } from "@/stores/user-store";
import { useQueryClient } from "@tanstack/react-query";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { AxiosError } from "axios";
import { ArrowUp, ArrowUpDown, Building2, ChevronDown, Edit, X } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetTrigger } from "./ui/sheet";
import { Skeleton } from "./ui/skeleton";

const SortHeaderButton = ({
  column,
  title,
}: {
  column: {
    toggleSorting: (desc?: boolean, isMulti?: boolean) => void;
    getIsSorted: () => false | "asc" | "desc";
    getSortIndex: () => number;
    getToggleSortingHandler: () => ((event: unknown) => void) | undefined;
    clearSorting: () => void;
  };
  title: string;
}) => {
  const isSorted = column.getIsSorted();
  const sortHandler = column.getToggleSortingHandler();

  const handleClick = (e: React.MouseEvent) => {
    if (e.altKey && isSorted) {
      e.preventDefault();
      e.stopPropagation();
      column.clearSorting();
    } else if (sortHandler) {
      sortHandler(e);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (isSorted) {
      e.preventDefault();
      column.clearSorting();
    }
  };

  return (
    <Button
      variant="ghost"
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className="h-auto p-0 hover:bg-transparent"
    >
      <span className="flex items-center gap-1">
        {title}
        {isSorted === "asc" ? (
          <ArrowUp className="h-4 w-4 rotate-180" />
        ) : isSorted === "desc" ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4" />
        )}
      </span>
    </Button>
  );
};

// Helper function to sort roles by privilege level (highest first)
const sortRolesByPrivilege = (roles: string[]): string[] => {
  const privilegeOrder: Record<string, number> = {
    SUPERADMIN: 1,
    ORGOWNER: 2,
    ORGVIEWER: 3,
  };

  return [...roles].sort((a, b) => {
    const orderA = privilegeOrder[a] || 999;
    const orderB = privilegeOrder[b] || 999;
    return orderA - orderB;
  });
};

export const UserManagementDrawer = ({
  user: targetUser,
  canManage,
  onRefresh,
  organizations,
}: {
  user: ListUser;
  canManage: boolean;
  onRefresh: () => void;
  organizations: { id: string; name: string }[];
}) => {
  const [open, setOpen] = useState(false);
  const [editingRoles, setEditingRoles] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [selectedRoleOrgId, setSelectedRoleOrgId] = useState<string>("");
  const [selectedRoles, setSelectedRoles] = useState<Record<string, boolean>>({
    ORGVIEWER: false,
    ORGOWNER: false,
    SUPERADMIN: false,
  });
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    display_name: "",
  });
  const [managingOrgs, setManagingOrgs] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [newOrgRoles, setNewOrgRoles] = useState<Record<string, boolean>>({ ORGVIEWER: true });
  const [savingOrgs, setSavingOrgs] = useState(false);
  const queryClient = useQueryClient();
  const currentUser = useUserStore.use.user();
  const isSuperAdmin = currentUser?.superAdmin;
  const isOrgOwner = currentUser?.roleKeys?.includes("ORGOWNER");

  const currentOrgIds = (targetUser.organizations ?? []).map((org) => org.id);
  const toAdd = selectedOrgIds.filter((id) => !currentOrgIds.includes(id));
  const toRemove = currentOrgIds.filter((id) => !selectedOrgIds.includes(id));
  const hasChanges = toAdd.length > 0 || toRemove.length > 0;
  const rolesQuery = useOrgsRoles(toAdd);
  const assignableRoles = rolesQuery.data.filter((role) => isSuperAdmin || role !== "SUPERADMIN");

  const userBelongsToTargetOrg =
    isSuperAdmin ||
    (currentUser?.organizations && targetUser.organization_id
      ? currentUser.organizations.includes(targetUser.organization_id)
      : false) ||
    false;

  const buildRoleState = (roles: string[]): Record<string, boolean> => {
    const roleState: Record<string, boolean> = { ORGVIEWER: false, ORGOWNER: false, SUPERADMIN: false };
    roles.forEach((role) => {
      if (Object.prototype.hasOwnProperty.call(roleState, role)) {
        roleState[role] = true;
      }
    });
    return roleState;
  };

  const rolesForOrg = useCallback(
    (orgId: string): string[] | undefined => {
      const perOrg = targetUser.orgRoles?.[orgId];
      if (perOrg) return perOrg;
      if (orgId === targetUser.organization_id) return targetUser.roleKeys ?? [];
      return undefined;
    },
    [targetUser.orgRoles, targetUser.organization_id, targetUser.roleKeys],
  );

  const hasRoleDataForSelectedOrg = rolesForOrg(selectedRoleOrgId) !== undefined;

  useEffect(() => {
    if (open) {
      const defaultOrgId = targetUser.organization_id || "";
      setSelectedRoleOrgId(defaultOrgId);
      setSelectedRoles(buildRoleState(rolesForOrg(defaultOrgId) ?? []));

      setProfileData({
        first_name: targetUser.human?.profile?.firstName || "",
        last_name: targetUser.human?.profile?.lastName || "",
        display_name: targetUser.human?.profile?.displayName || "",
      });

      setManagingOrgs(false);
    }
  }, [open, rolesForOrg, targetUser.human, targetUser.organization_id]);

  const handleEditOrgChange = (orgId: string) => {
    setSelectedRoleOrgId(orgId);
    setSelectedRoles(buildRoleState(rolesForOrg(orgId) ?? []));
  };

  const handleSaveRoles = async () => {
    const roleKeys = Object.keys(selectedRoles).filter(
      (key) => selectedRoles[key] && (isSuperAdmin || key !== "SUPERADMIN"),
    );

    if (!roleKeys.includes("ORGVIEWER") && !roleKeys.includes("ORGOWNER") && !roleKeys.includes("SUPERADMIN")) {
      roleKeys.push("ORGVIEWER");
    }

    const orgIdToUpdate = selectedRoleOrgId || targetUser.organization_id;
    if (!orgIdToUpdate) {
      toast.error("No organization selected for role update");
      return;
    }

    try {
      const grantId = targetUser.orgGrantIds?.[orgIdToUpdate];
      await useApiInstance.patch(`/api/v1/organizations/${orgIdToUpdate}/users/${targetUser.id}/roles`, {
        roleKeys,
        ...(grantId && { grantId }),
      });
      toast.success(`Roles updated for ${targetUser.email}`);
      setEditingRoles(false);
      onRefresh();
    } catch (error: unknown) {
      toast.error(`Failed to update roles: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await useApiInstance.post(`/api/v1/organizations/${targetUser.organization_id}/users/${targetUser.id}/update`, {
        profile: {
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          display_name: profileData.display_name,
        },
        email: {
          email: targetUser.email,
        },
        user_name: targetUser.email,
        role_keys: targetUser.roleKeys || [],
      });
      toast.success(`Profile updated for ${targetUser.email}`);
      setEditingProfile(false);
      // Zitadel's list projection lags the write, so a refetch here races it
      // and can pin stale data for the whole staleTime window. Patch the
      // cache with the values Zitadel just accepted instead.
      queryClient.setQueryData<ListUser[]>(queryKeys.allUsers, (users) =>
        users?.map((u) =>
          u.id === targetUser.id
            ? {
                ...u,
                name: profileData.display_name,
                human: {
                  ...u.human,
                  profile: {
                    ...u.human.profile,
                    firstName: profileData.first_name,
                    lastName: profileData.last_name,
                    displayName: profileData.display_name,
                  },
                },
              }
            : u,
        ),
      );
    } catch (error: unknown) {
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const openManageOrganizations = () => {
    setSelectedOrgIds(currentOrgIds);
    setNewOrgRoles({ ORGVIEWER: true });
    setOrgSearch("");
    setManagingOrgs(true);
  };

  const handleSaveOrganizations = async () => {
    if (savingOrgs) return;
    setSavingOrgs(true);

    const roleKeys = assignableRoles.filter((role) => role === "ORGVIEWER" || newOrgRoles[role]);
    if (roleKeys.length === 0) {
      roleKeys.push("ORGVIEWER");
    }

    const operations = [
      ...toAdd.map((orgId) => ({
        orgId,
        run: () =>
          useApiInstance.post(`/api/v1/organizations/${orgId}/users/${targetUser.id}/grant`, { role_keys: roleKeys }),
      })),
      ...toRemove.map((orgId) => ({
        orgId,
        run: () => useApiInstance.delete(`/api/v1/organizations/${orgId}/users/${targetUser.id}/grants`),
      })),
    ];

    try {
      const results = await Promise.allSettled(operations.map((operation) => operation.run()));
      const failures = results.flatMap((result, index) => {
        if (result.status !== "rejected") return [];
        const { orgId } = operations[index];
        const orgName = organizations.find((org) => org.id === orgId)?.name ?? orgId;
        return [`${orgName}: ${messageFromAxiosOrError(result.reason)}`];
      });

      if (failures.length === 0) {
        toast.success(`Organizations updated for ${targetUser.email}`);
      } else {
        toast.error(`Failed to update organizations: ${failures.join("; ")}`);
      }
      if (failures.length === operations.length) return;

      onRefresh();
      setManagingOrgs(false);
    } finally {
      setSavingOrgs(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await useApiInstance.patch(
        `/api/v1/organizations/${targetUser.organization_id}/users/${targetUser.id}/deactivate`,
      );
      toast.success(`User ${targetUser.email} deactivated`);
      setOpen(false);
      onRefresh();
    } catch (error: unknown) {
      toast.error(`Failed to deactivate user: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleReactivate = async () => {
    try {
      await useApiInstance.patch(
        `/api/v1/organizations/${targetUser.organization_id}/users/${targetUser.id}/reactivate`,
      );
      toast.success(`User ${targetUser.email} reactivated`);
      setOpen(false);
      onRefresh();
    } catch (error: unknown) {
      toast.error(`Failed to reactivate user: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleResendInvitation = async () => {
    try {
      await useApiInstance.post(
        `/api/v1/organizations/${targetUser.organization_id}/users/${targetUser.id}/resend-invitation`,
      );
      toast.success(`Invitation resent to ${targetUser.email}`);
    } catch (error: unknown) {
      toast.error(`Failed to resend invitation: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleDelete = async () => {
    const warningMessage = `⚠️ PERMANENT DELETION WARNING ⚠️

Are you sure you want to permanently delete ${targetUser.email}?

This will:
• Delete the user account from Zitadel entirely
• Remove all their access across ALL organizations
• This action CANNOT be undone

Type the user's email to confirm deletion.`;

    const confirmation = window.prompt(warningMessage);

    if (confirmation !== targetUser.email) {
      if (confirmation !== null) {
        toast.error("Email did not match. Deletion cancelled.");
      }
      return;
    }

    try {
      await useApiInstance.delete(`/api/v1/organizations/${targetUser.organization_id}/users/${targetUser.id}/delete`);
      toast.success(`User ${targetUser.email} has been permanently deleted`);
      setOpen(false);
      onRefresh();
    } catch (error: unknown) {
      toast.error(`Failed to delete user: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  if (!canManage) {
    return null; // Hide edit button if user can't manage
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline">
          <Edit />
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col" hideClose>
        {!userBelongsToTargetOrg && (
          <div className="mx-4 mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/20">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Limited Access:</strong> You do not belong to this user's organization. You cannot modify their
              details.
            </p>
          </div>
        )}
        <div className="grid flex-1 auto-rows-min gap-4 overflow-y-auto px-4 py-4">
          {/* Profile Details Section */}
          <div className="grid gap-3 border-b pb-3">
            <div className="mb-1 flex items-center justify-between">
              <Label className="text-base font-semibold">Manage User</Label>
              {!editingProfile && userBelongsToTargetOrg && (
                <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                  Edit Profile
                </Button>
              )}
            </div>
            {!editingProfile ? (
              <div className="grid gap-2">
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <div className="text-sm">{targetUser.name}</div>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <div className="text-sm">{targetUser.email}</div>
                </div>
                <div className="grid gap-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Organizations</Label>
                    {(isSuperAdmin || isOrgOwner) && userBelongsToTargetOrg && targetUser.organizations && (
                      <Button variant="outline" size="sm" onClick={openManageOrganizations}>
                        Modify
                      </Button>
                    )}
                  </div>
                  {(() => {
                    const allOrgs = targetUser.organizations ?? [];
                    const primaryOrgId = targetUser.organization_id;

                    // Filter to only show orgs that the current user has access to
                    const currentUserOrgs = currentUser?.organizations || [];
                    const visibleOrgs = isSuperAdmin
                      ? allOrgs
                      : allOrgs.filter((org) => currentUserOrgs.includes(org.id));

                    if (visibleOrgs.length === 0)
                      return <div className="text-sm text-muted-foreground">No organizations</div>;

                    return (
                      <div className="flex flex-wrap gap-1">
                        {visibleOrgs.map((org) => {
                          const isPrimary = org.id === primaryOrgId;

                          return (
                            <TooltipProvider key={org.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="cursor-help text-xs">
                                    {org.name}
                                    {isPrimary && <span className="ml-1">★</span>}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="space-y-1">
                                    <p className="font-medium">{org.name}</p>
                                    <p className="text-xs text-muted-foreground">ID: {org.id}</p>
                                    {isPrimary && <p className="text-xs">★ Primary organization</p>}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <Dialog open={managingOrgs} onOpenChange={setManagingOrgs}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Manage Organizations</DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-4">
                        <Input
                          placeholder="Search organizations..."
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                        />
                        <div className="max-h-64 space-y-2 overflow-y-auto">
                          {organizations
                            .filter((org) => org.name.toLowerCase().includes(orgSearch.toLowerCase()))
                            .map((org) => (
                              <div key={org.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`manage-org-${org.id}`}
                                  checked={selectedOrgIds.includes(org.id)}
                                  disabled={savingOrgs || org.id === targetUser.home_organization_id}
                                  onCheckedChange={(checked) =>
                                    setSelectedOrgIds((prev) =>
                                      checked ? [...prev, org.id] : prev.filter((id) => id !== org.id),
                                    )
                                  }
                                />
                                <Label htmlFor={`manage-org-${org.id}`} className="text-sm font-normal">
                                  {org.name}
                                </Label>
                              </div>
                            ))}
                        </div>
                        {toAdd.length > 0 && (
                          <div className="grid gap-2">
                            <Label className="text-sm">Roles for new organizations</Label>
                            {rolesQuery.isLoading ? (
                              <Skeleton className="h-6 w-full" />
                            ) : rolesQuery.isError ? (
                              <p className="text-sm text-destructive">
                                Unable to load organization roles. Please try again.
                              </p>
                            ) : (
                              assignableRoles.map((grant) => (
                                <div key={grant} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`manage-role-${grant}`}
                                    checked={grant === "ORGVIEWER" || (newOrgRoles[grant] ?? false)}
                                    disabled={savingOrgs || grant === "ORGVIEWER"}
                                    onCheckedChange={(checked) =>
                                      setNewOrgRoles((prev) => ({ ...prev, [grant]: !!checked }))
                                    }
                                  />
                                  <Label htmlFor={`manage-role-${grant}`} className="text-sm font-normal">
                                    {grant.replace("ORG", "")}
                                  </Label>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button
                          onClick={handleSaveOrganizations}
                          disabled={
                            savingOrgs ||
                            !hasChanges ||
                            (toAdd.length > 0 && (rolesQuery.isLoading || rolesQuery.isError))
                          }
                        >
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge className="w-fit">{targetUser.state}</Badge>
                </div>
              </div>
            ) : (
              <div className="grid gap-2">
                <div className="grid gap-1">
                  <Label htmlFor="edit-first-name" className="text-xs">
                    First Name
                  </Label>
                  <Input
                    id="edit-first-name"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                    placeholder="Enter first name..."
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="edit-last-name" className="text-xs">
                    Last Name
                  </Label>
                  <Input
                    id="edit-last-name"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                    placeholder="Enter last name..."
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="edit-display-name" className="text-xs">
                    Display Name
                  </Label>
                  <Input
                    id="edit-display-name"
                    value={profileData.display_name}
                    onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                    placeholder="Enter display name..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveProfile}>
                    Save Profile
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Roles Section */}
          <div className="grid gap-2 border-b pb-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Roles</Label>
              {!editingRoles && userBelongsToTargetOrg && (
                <Button variant="outline" size="sm" onClick={() => setEditingRoles(true)}>
                  Edit
                </Button>
              )}
            </div>
            {!editingRoles ? (
              <div className="grid gap-2">
                {(() => {
                  const userOrgs = targetUser.organizations ?? [];
                  const visibleOrgs = isSuperAdmin
                    ? userOrgs
                    : userOrgs.filter((org) => currentUser?.organizations?.includes(org.id));

                  if (visibleOrgs.length > 1) {
                    return (
                      <>
                        <Select value={selectedRoleOrgId} onValueChange={setSelectedRoleOrgId}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select organization..." />
                          </SelectTrigger>
                          <SelectContent>
                            {visibleOrgs.map((org) => (
                              <SelectItem key={org.id} value={org.id}>
                                {org.name}
                                {org.id === targetUser.organization_id && " ★"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex flex-wrap gap-1">
                          {sortRolesByPrivilege(targetUser.orgRoles?.[selectedRoleOrgId] || []).map((role) => (
                            <Badge
                              key={role}
                              variant={role === "SUPERADMIN" ? "destructive" : "outline"}
                              className="text-xs"
                            >
                              {role.replace("ORG", "")}
                            </Badge>
                          ))}
                        </div>
                      </>
                    );
                  }

                  return (
                    <div className="flex flex-wrap gap-1">
                      {sortRolesByPrivilege(targetUser.roleKeys || []).map((role) => (
                        <Badge
                          key={role}
                          variant={role === "SUPERADMIN" ? "destructive" : "outline"}
                          className="text-xs"
                        >
                          {role.replace("ORG", "")}
                        </Badge>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="grid gap-2">
                {(() => {
                  const userOrgs = targetUser.organizations ?? [];
                  const visibleOrgs = isSuperAdmin
                    ? userOrgs
                    : userOrgs.filter((org) => currentUser?.organizations?.includes(org.id));

                  const editableOrgs = isSuperAdmin
                    ? visibleOrgs
                    : visibleOrgs.filter((org) => org.id === targetUser.organization_id);

                  if (editableOrgs.length > 1) {
                    return (
                      <div className="mb-2">
                        <Label className="mb-1 text-xs text-muted-foreground">Editing roles for:</Label>
                        <Select value={selectedRoleOrgId} onValueChange={handleEditOrgChange}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select organization..." />
                          </SelectTrigger>
                          <SelectContent>
                            {editableOrgs.map((org) => (
                              <SelectItem key={org.id} value={org.id}>
                                {org.name}
                                {org.id === targetUser.organization_id && " ★"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  } else if (editableOrgs.length === 1) {
                    return (
                      <div className="mb-2">
                        <Label className="mb-1 text-xs text-muted-foreground">Editing roles for:</Label>
                        <div className="text-sm font-medium">
                          {editableOrgs[0].name}
                          {editableOrgs[0].id === targetUser.organization_id && " ★"}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                {isSuperAdmin && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="role-superadmin"
                      checked={selectedRoles.SUPERADMIN}
                      onCheckedChange={(checked) => setSelectedRoles({ ...selectedRoles, SUPERADMIN: !!checked })}
                    />
                    <Label htmlFor="role-superadmin" className="text-sm font-normal">
                      SUPERADMIN
                    </Label>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-orgowner"
                    checked={selectedRoles.ORGOWNER}
                    onCheckedChange={(checked) => setSelectedRoles({ ...selectedRoles, ORGOWNER: !!checked })}
                  />
                  <Label htmlFor="role-orgowner" className="text-sm font-normal">
                    OWNER
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-orgviewer"
                    checked={selectedRoles.ORGVIEWER}
                    disabled={selectedRoles.ORGOWNER || selectedRoles.SUPERADMIN}
                    onCheckedChange={(checked) => setSelectedRoles({ ...selectedRoles, ORGVIEWER: !!checked })}
                  />
                  <Label htmlFor="role-orgviewer" className="text-sm font-normal">
                    VIEWER{" "}
                    {selectedRoles.ORGOWNER || selectedRoles.SUPERADMIN
                      ? "(included with higher roles)"
                      : "(required if no higher role)"}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button variant="default" size="sm" onClick={handleSaveRoles} disabled={!hasRoleDataForSelectedOrg}>
                    Save
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingRoles(false)}>
                    Cancel
                  </Button>
                </div>
                {!hasRoleDataForSelectedOrg && (
                  <p className="text-xs text-muted-foreground">
                    No role data available for this organization. The user may not have a grant here yet.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* User Actions Section */}
          <div className="grid gap-2">
            <Label className="text-sm font-medium">Actions</Label>
            {userBelongsToTargetOrg ? (
              (() => {
                const isSelf = currentUser?.email === targetUser.email || currentUser?.sub === targetUser.id;

                if (isSelf) {
                  return <div className="text-sm text-muted-foreground">You cannot deactivate or delete yourself</div>;
                }

                return (
                  <div className="grid gap-2">
                    {(targetUser.state === "Initial" || targetUser.state === "Invited") && (
                      <>
                        <Button variant="outline" size="sm" onClick={handleResendInvitation}>
                          Resend Invitation
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => copyInviteLink(targetUser)}>
                          Copy Invite Link
                        </Button>
                      </>
                    )}
                    {targetUser.state !== "Inactive" && (
                      <Button variant="destructive" size="sm" onClick={handleDeactivate}>
                        Deactivate User
                      </Button>
                    )}
                    {targetUser.state === "Inactive" && (
                      <Button variant="default" size="sm" onClick={handleReactivate}>
                        Reactivate User
                      </Button>
                    )}
                    <Button variant="destructive" size="sm" onClick={handleDelete}>
                      Delete User Permanently
                    </Button>
                  </div>
                );
              })()
            ) : (
              <div className="text-sm text-muted-foreground">
                No actions available - you don't belong to this user's organization
              </div>
            )}
          </div>
        </div>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline">Close</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export function DataTable() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "organization_name", desc: false },
    { id: "name", desc: false },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(() => {
    const saved = localStorage.getItem("users-table-pagination");
    return saved ? JSON.parse(saved) : { pageIndex: 0, pageSize: 50 };
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const user = useUserStore.use.user();
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [invitationOpen, setInvitationOpen] = useState(false);
  const [copyInviteLinkOnInvite, setCopyInviteLinkOnInvite] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string[]>([]);
  const [grantsForm, setGrantsForm] = useState<Record<string, boolean>>({ ORGVIEWER: true });
  const [stateFilters, setStateFilters] = useState<string[]>([]);
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [inviteOrgId, setInviteOrgId] = useState<string>("");
  const [manageOrgsOpen, setManageOrgsOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const selectedOrg = useOrgStore.use.selectedOrg();
  const orgs = useOrgStore.use.orgs();

  React.useEffect(() => {
    localStorage.setItem("users-table-pagination", JSON.stringify(pagination));
  }, [pagination]);

  const usersQuery = useAllUsers();
  const orgsQuery = useMyOrganizations();
  const orgRolesQuery = useOrgRoles(invitationOpen ? inviteOrgId : "");
  const availableGrants = (orgRolesQuery.data ?? []).filter((role) => user?.superAdmin || role !== "SUPERADMIN");
  const invalidateUsers = useInvalidateUsers();
  const refreshMutation = useRefreshUsers();

  const prepareOrgUsers = useCallback((orgUsers: ListUser[]) => {
    return orgUsers.map((user) => {
      const state = (() => {
        let s = user.state;
        if (s.startsWith("USER_STATE_")) {
          s = s.replace("USER_STATE_", "");
        } else {
          s = "unknown";
        }
        s = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
        // v2-created users are Active from creation; an unverified email means
        // they have not completed their invite yet.
        if (s === "Active" && !user.human.email.isEmailVerified) {
          s = "Invited";
        }
        return s;
      })();

      const userWithOrgs = user as ListUser & {
        organizations?: Array<{ id: string; name: string }>;
        orgRoles?: Record<string, string[]>;
      };

      return {
        ...user,
        name: user.human.profile.displayName || "No Display Name",
        email: user.human.email.email || "No Email",
        state,
        organizations: userWithOrgs.organizations || [],
        orgRoles: userWithOrgs.orgRoles || {},
      };
    });
  }, []);

  const users = useMemo(() => {
    if (!usersQuery.data) return [];
    const allUsers = prepareOrgUsers(usersQuery.data);

    // Super admins can see all users
    if (user?.superAdmin) {
      return allUsers;
    }

    // Regular users can only see users from their own organizations
    const currentUserOrgs = user?.organizations || [];
    return allUsers.filter((u) => {
      const userOrgs = (u as ListUser & { organizations?: Array<{ id: string; name: string }> }).organizations || [];
      // Check if the user belongs to at least one org that the current user also belongs to
      return userOrgs.some((org) => currentUserOrgs.includes(org.id));
    });
  }, [usersQuery.data, prepareOrgUsers, user?.superAdmin, user?.organizations]);

  const organizations = orgsQuery.data || [];
  const loading = usersQuery.isLoading || orgsQuery.isLoading;

  const handleOrgManagementSuccess = () => {
    invalidateUsers();
  };

  const handleRefresh = async () => {
    const toastId = toast.loading("Refreshing user and organization data...");
    try {
      await refreshMutation.mutateAsync();
      setLastUpdated(new Date());
      toast.dismiss(toastId);
    } catch (_error) {
      toast.error("Failed to refresh user and organization data", { id: toastId });
    }
  };

  const formatLastUpdated = (date: Date) => {
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // If the API explicitly returned 403, the local token is stale after a role downgrade.
  // Only that status revokes access — transient errors (5xx, network failures) do not.
  const isPermissionDenied =
    usersQuery.isError && usersQuery.error instanceof AxiosError && usersQuery.error.response?.status === 403;
  const hasAccess = (user?.superAdmin || user?.roleKeys?.includes("ORGOWNER")) && !isPermissionDenied;
  const canManageUsers = user?.superAdmin || user?.roleKeys?.includes("ORGOWNER");

  const columns: ColumnDef<ListUser>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => {
        return <SortHeaderButton column={column} title="Name" />;
      },
      cell: ({ row }) => <div className="capitalize">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "email",
      header: ({ column }) => {
        return <SortHeaderButton column={column} title="Email" />;
      },
      cell: ({ row }) => <div className="lowercase">{row.getValue("email")}</div>,
    },
    {
      accessorKey: "organization_name",
      header: ({ column }) => {
        return <SortHeaderButton column={column} title="Organization" />;
      },
      filterFn: (row, _columnId, filterValue) => {
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
        const user = row.original as ListUser & { organizations: Array<{ id: string; name: string }> };
        const orgNames = user.organizations?.map((org) => org.name) || [];
        return Array.isArray(filterValue)
          ? filterValue.some((fv) => orgNames.includes(fv))
          : orgNames.includes(filterValue);
      },
      cell: ({ row }) => {
        const targetUser = row.original as ListUser & { organizations: Array<{ id: string; name: string }> };
        const allOrgs = targetUser.organizations || [];
        const primaryOrgId = targetUser.organization_id;

        // Filter to only show orgs that the current user has access to
        const currentUserOrgs = user?.organizations || [];
        const visibleOrgs = user?.superAdmin ? allOrgs : allOrgs.filter((org) => currentUserOrgs.includes(org.id));

        if (visibleOrgs.length === 0) return <div>N/A</div>;

        // Sort organizations: primary first, then alphabetically
        const sortedOrgs = [...visibleOrgs].sort((a, b) => {
          if (a.id === primaryOrgId) return -1;
          if (b.id === primaryOrgId) return 1;
          return a.name.localeCompare(b.name);
        });

        const displayOrgs = sortedOrgs.slice(0, 3);
        const remainingCount = sortedOrgs.length - 3;

        return (
          <div className="flex flex-wrap gap-1">
            {displayOrgs.map((org) => {
              const isPrimary = org.id === primaryOrgId;
              return (
                <TooltipProvider key={org.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-xs">
                        {org.name}
                        {isPrimary && <span className="ml-1">★</span>}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{isPrimary ? "Primary organization" : "Additional organization"}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
            {remainingCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-xs">
                      +{remainingCount}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      {sortedOrgs.slice(3).map((org) => (
                        <div key={org.id}>{org.name}</div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "roleKeys",
      header: () => {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex cursor-help items-center">Roles</div>
              </TooltipTrigger>
              <TooltipContent>Shows all unique roles across all organizations</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      filterFn: (row, _columnId, filterValue) => {
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
        const user = row.original as ListUser & { orgRoles?: Record<string, string[]> };
        const orgRoles = user.orgRoles || {};
        const allRoles = Object.values(orgRoles).flat();
        const uniqueRoles = Array.from(new Set(allRoles));

        if (Array.isArray(filterValue)) {
          return filterValue.some((role) => uniqueRoles.includes(role));
        }
        return uniqueRoles.includes(filterValue);
      },
      cell: ({ row }) => {
        const user = row.original as ListUser & { orgRoles?: Record<string, string[]> };
        const orgRoles = user.orgRoles || {};
        const allRoles = Object.values(orgRoles).flat();
        const uniqueRoles = Array.from(new Set(allRoles));

        if (uniqueRoles.length === 0) return <div className="text-sm text-muted-foreground">No Roles</div>;

        const sortedRoles = sortRolesByPrivilege(uniqueRoles);
        const displayRoles = sortedRoles.slice(0, 3);
        const remainingCount = sortedRoles.length - 3;

        return (
          <div className="flex flex-wrap gap-1">
            {displayRoles.map((role) => (
              <Badge key={role} variant={role === "SUPERADMIN" ? "destructive" : "outline"} className="text-xs">
                {role.replace("ORG", "")}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-xs">
                      +{remainingCount}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      {sortedRoles.slice(3).map((role) => (
                        <div key={role}>{role.replace("ORG", "")}</div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "state",
      enableSorting: true,
      enableHiding: false,
      header: ({ column }) => {
        return <SortHeaderButton column={column} title="State" />;
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
        const state: string = row.getValue(columnId);
        return Array.isArray(filterValue) ? filterValue.includes(state) : filterValue === state;
      },
      cell: ({ row }) => <div className="capitalize">{row.getValue("state")}</div>,
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        return [
          <div className="flex justify-end" key="actions">
            <UserManagementDrawer
              key="edit-user"
              user={row.original}
              canManage={canManageUsers || false}
              onRefresh={invalidateUsers}
              organizations={organizations}
            />
          </div>,
        ];
      },
    },
  ];

  const sendInvite = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!firstName || !lastName || !trimmedEmail || !inviteOrgId) {
      toast.error("Please fill in all fields including organization");
      return;
    }

    const grantsToSubmit = availableGrants.filter((key) => grantsForm[key]);

    useApiInstance
      .post<{ user_id?: string }>(`/api/v1/organizations/${inviteOrgId}/users/add`, {
        profile: {
          first_name: firstName,
          last_name: lastName,
          display_name: `${firstName} ${lastName}`,
        },
        email: {
          email: trimmedEmail,
        },
        user_name: trimmedEmail,
        role_keys: grantsToSubmit,
      })
      .then(async (response) => {
        toast.success(`Invitation sent to ${trimmedEmail}`);
        if (copyInviteLinkOnInvite) {
          const userId = response.data?.user_id;
          if (userId) {
            await copyInviteLink({ id: userId, organization_id: inviteOrgId, email: trimmedEmail });
          } else {
            toast.error("Could not generate invite link: user id missing from response");
          }
        }
        setFirstName("");
        setLastName("");
        setEmail("");
        setInviteOrgId("");
        setGrantsForm({ ORGVIEWER: true });
        setCopyInviteLinkOnInvite(false);
        invalidateUsers();
      })
      .catch((error) => {
        const detail = messageFromAxiosOrError(error);
        const normalizedDetail = detail.toLowerCase();
        if (error instanceof AxiosError && error.response?.status === 409) {
          const title = normalizedDetail.includes("already a member")
            ? "Already in this organization"
            : "User already exists";
          toast.error(title, {
            description: detail,
            duration: 10000,
          });
          return;
        }
        toast.error(`Error sending invitation: ${detail}`);
      })
      .finally(() => {
        setInvitationOpen(false);
      });
  };

  useEffect(() => {
    if (users.length > 0) {
      setStateFilters(Array.from(new Set(users.map((user) => user.state))).sort());
      const allRoles = users.flatMap((user) => user.roleKeys || []);
      setRoleFilters(Array.from(new Set(allRoles)).sort());
    }
  }, [users]);

  useEffect(() => {
    setPagination((prev: { pageIndex: number; pageSize: number }) => ({ ...prev, pageIndex: 0 }));
  }, [columnFilters, globalFilter]);

  const table = useReactTable({
    data: users,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    enableMultiSort: true,
    enableSortingRemoval: false,
    maxMultiSortColCount: 5,
    globalFilterFn: (row, _columnId, filterValue) => {
      const searchValue = String(filterValue).toLowerCase();

      const name = String(row.getValue("name") || "").toLowerCase();
      const email = String(row.getValue("email") || "").toLowerCase();
      const user = row.original as ListUser & { organizations?: Array<{ id: string; name: string }> };
      const orgNames = user.organizations?.map((org) => org.name.toLowerCase()).join(" ") || "";
      const state = String(row.getValue("state") || "").toLowerCase();
      const roles = (row.getValue("roleKeys") as string[]) || [];
      const rolesString = roles.join(" ").toLowerCase();

      return (
        name.includes(searchValue) ||
        email.includes(searchValue) ||
        orgNames.includes(searchValue) ||
        state.includes(searchValue) ||
        rolesString.includes(searchValue)
      );
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  if (!hasAccess) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">You do not have permission to view this page.</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {orgs.length > 1 && selectedOrg && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20">
          <Badge
            variant="outline"
            className="border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-200"
          >
            Active Org: {selectedOrg.name}
          </Badge>
          <span className="text-sm text-muted-foreground">Use filters to show users from specific organizations</span>
        </div>
      )}
      {usersQuery.isError && (
        <Alert variant="default" className="mb-4 border-destructive/30 bg-destructive/10">
          <AlertDescription className="text-destructive">
            Failed to load users: {messageFromAxiosOrError(usersQuery.error)}
            {usersQuery.data ? " Showing the last successful result." : ""}
          </AlertDescription>
        </Alert>
      )}
      <div className="justify-end-safe flex py-4">
        <div className="mr-2 w-64">
          <Input
            placeholder="Search users..."
            value={globalFilter ?? ""}
            onChange={(event) => table.setGlobalFilter(String(event.target.value))}
          />
        </div>
        <div className="mr-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={
                  selectedStates.length > 0 || selectedOrgs.length > 0 || selectedRoles.length > 0
                    ? "w-[200px] justify-between border-primary"
                    : "w-[200px] justify-between"
                }
              >
                <span className="flex items-center gap-2">
                  Filters
                  {(selectedStates.length > 0 || selectedOrgs.length > 0 || selectedRoles.length > 0) && (
                    <Badge variant="secondary" className="h-5 px-1">
                      {selectedStates.length + selectedOrgs.length + selectedRoles.length}
                    </Badge>
                  )}
                </span>
                {selectedStates.length > 0 || selectedOrgs.length > 0 || selectedRoles.length > 0 ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className="flex h-7 w-8 items-center justify-center rounded-sm border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      table.getColumn("state")?.setFilterValue([]);
                      table.getColumn("organization_name")?.setFilterValue([]);
                      table.getColumn("roleKeys")?.setFilterValue([]);
                      setSelectedStates([]);
                      setSelectedOrgs([]);
                      setSelectedRoles([]);
                      toast.success("Filters cleared");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        table.getColumn("state")?.setFilterValue([]);
                        table.getColumn("organization_name")?.setFilterValue([]);
                        table.getColumn("roleKeys")?.setFilterValue([]);
                        setSelectedStates([]);
                        setSelectedOrgs([]);
                        setSelectedRoles([]);
                        toast.success("Filters cleared");
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </span>
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
              <div className="space-y-4 p-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-muted-foreground">FILTERS</Label>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">ORGANIZATION</Label>
                  <div className="max-h-[180px] space-y-2 overflow-y-auto">
                    {organizations.map((org) => (
                      <div key={org.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`org-${org.id}`}
                          checked={selectedOrgs.includes(org.name)}
                          onCheckedChange={(checked) => {
                            const newOrgs = checked
                              ? [...selectedOrgs, org.name]
                              : selectedOrgs.filter((o) => o !== org.name);
                            setSelectedOrgs(newOrgs);
                            table
                              .getColumn("organization_name")
                              ?.setFilterValue(newOrgs.length > 0 ? newOrgs : undefined);
                          }}
                        />
                        <label
                          htmlFor={`org-${org.id}`}
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {org.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">ROLE</Label>
                  <div className="max-h-[180px] space-y-2 overflow-y-auto">
                    {roleFilters.map((role) => (
                      <div key={role} className="flex items-center space-x-2">
                        <Checkbox
                          id={`role-${role}`}
                          checked={selectedRoles.includes(role)}
                          onCheckedChange={(checked) => {
                            const newRoles = checked
                              ? [...selectedRoles, role]
                              : selectedRoles.filter((r) => r !== role);
                            setSelectedRoles(newRoles);
                            table.getColumn("roleKeys")?.setFilterValue(newRoles.length > 0 ? newRoles : undefined);
                          }}
                        />
                        <label
                          htmlFor={`role-${role}`}
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {role.replace("ORG", "")}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">STATE</Label>
                  <div className="max-h-[180px] space-y-2 overflow-y-auto">
                    {stateFilters.map((state) => (
                      <div key={state} className="flex items-center space-x-2">
                        <Checkbox
                          id={`state-${state}`}
                          checked={selectedStates.includes(state)}
                          onCheckedChange={(checked) => {
                            const newStates = checked
                              ? [...selectedStates, state]
                              : selectedStates.filter((s) => s !== state);
                            setSelectedStates(newStates);
                            table.getColumn("state")?.setFilterValue(newStates.length > 0 ? newStates : undefined);
                          }}
                        />
                        <label
                          htmlFor={`state-${state}`}
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {state}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex-1" />
        {user?.superAdmin && (
          <div className="mr-2">
            <Button variant="outline" onClick={() => setManageOrgsOpen(true)}>
              <Building2 className="mr-2 h-4 w-4" />
              Manage Organizations
            </Button>
          </div>
        )}
        <div className="mr-2 max-w-sm">
          <Dialog
            open={invitationOpen}
            onOpenChange={(open) => {
              setInvitationOpen(open);
              if (!open) {
                setFirstName("");
                setLastName("");
                setEmail("");
                setInviteOrgId("");
                setGrantsForm({ ORGVIEWER: true });
                setCopyInviteLinkOnInvite(false);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button variant="green">Invite User</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <form onSubmit={sendInvite}>
                <DialogHeader>
                  <DialogTitle>Invite User</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4">
                  <div className="grid gap-3">
                    <Label htmlFor="name-1">First Name</Label>
                    <Input id="name-1" name="name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="last_name-1">Last Name</Label>
                    <Input
                      id="last_name-1"
                      name="last_name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="email-1">Email</Label>
                    <Input
                      id="email-1"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="org-select">Organization *</Label>
                    <Combobox
                      options={organizations.map((org) => ({ value: org.id, label: org.name }))}
                      value={inviteOrgId}
                      onValueChange={(orgId) => {
                        setInviteOrgId(orgId);
                        setGrantsForm({ ORGVIEWER: true });
                      }}
                      placeholder="Select organization..."
                      searchPlaceholder="Search organizations..."
                      emptyText="No organizations found."
                    />
                  </div>
                  {inviteOrgId &&
                    (orgRolesQuery.isLoading ? (
                      <Skeleton className="h-6 w-full" />
                    ) : orgRolesQuery.isError ? (
                      <p className="text-sm text-destructive">Unable to load organization roles. Please try again.</p>
                    ) : (
                      availableGrants.map((grant: string) => (
                        <div key={grant} className="flex">
                          {grant === "ORGVIEWER" ? (
                            <Checkbox id={grant} name={grant} checked={true} className="mr-2" disabled={true} />
                          ) : (
                            <Checkbox
                              id={grant}
                              name={grant}
                              checked={grantsForm[grant] ?? false}
                              className="mr-2"
                              onCheckedChange={(value) => {
                                setGrantsForm((prev) => ({
                                  ...prev,
                                  [grant]: !!value,
                                  ORGVIEWER: true,
                                }));
                              }}
                            />
                          )}
                          <Label htmlFor={grant} className="grow">
                            {grant.replace("ORG", "")}
                          </Label>
                        </div>
                      ))
                    ))}
                  <Separator />
                  <div className="flex">
                    <Checkbox
                      id="copy-invite-link-on-invite"
                      checked={copyInviteLinkOnInvite}
                      className="mr-2"
                      onCheckedChange={(value) => setCopyInviteLinkOnInvite(!!value)}
                    />
                    <Label htmlFor="copy-invite-link-on-invite" className="grow">
                      Copy invite link for manual delivery
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="secondary">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button type="submit" variant="green">
                    Invite
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mr-2 max-w-sm">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="ml-auto"
                  onClick={handleRefresh}
                  disabled={refreshMutation.isPending}
                >
                  Refresh
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Last updated: {formatLastUpdated(lastUpdated)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-xs text-muted-foreground">
          Showing {table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} users
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <p className="text-xs font-medium">Users per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-7 w-[60px] text-xs">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 25, 50, 100, 200].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[90px] items-center justify-center text-xs font-medium">
            Page {table.getPageCount() === 0 ? 0 : table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              className="h-7 w-7 p-0 text-xs"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <span>⟪</span>
            </Button>
            <Button
              variant="outline"
              className="h-7 w-7 p-0 text-xs"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <span>‹</span>
            </Button>
            <Button
              variant="outline"
              className="h-7 w-7 p-0 text-xs"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <span>›</span>
            </Button>
            <Button
              variant="outline"
              className="h-7 w-7 p-0 text-xs"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <span>⟫</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length > 0 &&
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            {loading && (
              <>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell className="text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                    <TableCell className="w-140 text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-6" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!loading && !usersQuery.isError && table.getRowModel().rows?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ManageOrganizationsDialog
        open={manageOrgsOpen}
        onOpenChange={setManageOrgsOpen}
        onSuccess={handleOrgManagementSuccess}
      />
    </div>
  );
}
